import { supabase } from "../supabase";

// crm-api's assistant routes (backend/services/crm-api/src/assistant.js and
// assistantKeys.js). Same origin: Caddy routes /api/v1/* to crm-api in
// production, Vite's proxy does locally. Every call carries the founder's own
// Supabase session token; crm-api checks it with GoTrue.

export type FieldValues = Record<string, string | number | null>;
export type Change = {
  kind: "created" | "updated";
  entity_type: "company" | "contact";
  id: number;
  label: string;
  before: FieldValues | null;
  after: FieldValues;
};
export type Previous = { text: string; question: string };
export type Outcome =
  | { ok: true; summary: string; changes: Change[]; question?: string }
  | { ok: false; code: string; message: string; changes: Change[] };

/** The founder's own Anthropic key, as crm-api reports it: never the key itself. */
export type KeyStatus = { saved: boolean; last4: string | null; updated_at: string | null };
export type KeyResult = { ok: true; status: KeyStatus } | { ok: false; code: string; message: string };

const MESSAGES: Record<string, string> = {
  unauthorized: "Your session has expired. Sign in again.",
  no_active_account: "This account is not one of the CRM's founders.",
  auth_unavailable: "Sign-in could not be checked just now. Try again.",
  upstream_error: "The CRM's data could not be read just now. Try again.",
};

type Body = { error?: string; message?: string; [k: string]: unknown } | null;

async function call(method: string, path: string, payload?: unknown): Promise<{ res: Response | null; body: Body }> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw new Error(error.message);
  const token = data.session?.access_token;
  if (!token) return { res: null, body: { error: "unauthorized" } };
  const res = await fetch(path, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(payload === undefined ? {} : { "Content-Type": "application/json" }) },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
  // A non-JSON answer (a proxy's error page) is reported below by its HTTP status.
  const body = (await res.json().catch(() => null)) as Body;
  return { res, body };
}

function failure(res: Response | null, body: Body): { code: string; message: string } {
  const code = body?.error ?? `http_${res?.status ?? 0}`;
  return { code, message: MESSAGES[code] ?? body?.message ?? `The request failed (HTTP ${res?.status ?? "?"}).` };
}

export async function sendInstruction(text: string, previous: Previous | null): Promise<Outcome> {
  const { res, body } = await call("POST", "/api/v1/assistant", previous ? { text, previous } : { text });
  const changes = (body?.changes as Change[] | undefined) ?? [];
  if (res?.ok && body && typeof body.summary === "string") {
    const question = typeof body.question === "string" && body.question ? { question: body.question } : {};
    return { ok: true, summary: body.summary, changes, ...question };
  }
  return { ok: false, ...failure(res, body), changes };
}

async function keyCall(method: "GET" | "PUT" | "DELETE", key?: string): Promise<KeyResult> {
  const { res, body } = await call(method, "/api/v1/assistant/key", key === undefined ? undefined : { key });
  if (res?.ok && body && typeof body.saved === "boolean") {
    return { ok: true, status: { saved: body.saved, last4: (body.last4 as string | null) ?? null, updated_at: (body.updated_at as string | null) ?? null } };
  }
  return { ok: false, ...failure(res, body) };
}

export const getKey = () => keyCall("GET");
export const saveKey = (key: string) => keyCall("PUT", key);
export const removeKey = () => keyCall("DELETE");

export function recordHref(c: Pick<Change, "entity_type" | "id">): string {
  return c.entity_type === "company" ? `#/companies/${c.id}/show` : `#/contacts/${c.id}/show`;
}
